import type { ComponentProps, FC } from "react";

import { Input } from "../../components";
import { cn } from "../../utils";
import { FIELD_BORDERLESS_CLASS_NAME } from "./field.constants";
import { FieldBase, type FieldBaseProps } from "./field-base";
import { useField } from "./use-field";

export type FieldInputProps = FieldBaseProps & {
  placeholder?: string;
  borderless?: boolean;
  type?: ComponentProps<typeof Input>["type"];
  onChange?: ComponentProps<typeof Input>["onChange"];
  onFocus?: ComponentProps<typeof Input>["onFocus"];
  onBlur?: ComponentProps<typeof Input>["onBlur"];
  inputProps?: ComponentProps<typeof Input>;
};

export const FieldInput: FC<FieldInputProps> = ({
  placeholder,
  borderless,
  type,
  onChange,
  onFocus,
  onBlur,
  inputProps,
  readOnly,
  ...props
}) => {
  const { field, hasError } = useField({ name: props.name, disabled: props.disabled, inputProps });

  const handleChange: FieldInputProps["onChange"] = (event) => {
    field.onChange(event);
    onChange?.(event);
  };

  const handleFocus: FieldInputProps["onFocus"] = (event) => {
    onFocus?.(event);
  };

  const handleBlur: FieldInputProps["onBlur"] = (event) => {
    field.onBlur();
    onBlur?.(event);
  };

  return (
    <FieldBase {...props}>
      <Input
        readOnly={readOnly}
        maxLength={props.maxLength}
        {...field}
        {...inputProps}
        type={type}
        placeholder={placeholder}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className={cn(
          {
            "border-destructive": hasError && !field.disabled,
            [FIELD_BORDERLESS_CLASS_NAME]: borderless,
          },
          inputProps?.className
        )}
      />
    </FieldBase>
  );
};
